import React from "react";
import { Link } from "react-router-dom";
import Ad from "../components/Ad";
import AffiliateCTA from "../components/affiliate/AffiliateCTA";
import AffiliateDisclosureInline from "../components/legal/AffiliateDisclosureInline";
import useReveal from "../hooks/useReveal";
import { Helmet } from "react-helmet";

export default function Home() {
  useReveal();

  return (
    <>
      <Helmet>
        <title>AI & Tech Today — Practical AI Tools & Tutorials (2025)</title>

        <meta
          name="description"
          content="AI & Tech Today provides clear, practical guides to modern AI tools like ChatGPT, Gemini, Copilot, and Claude, with tutorials, workflows, and real-world examples."
        />

        <link rel="canonical" href="https://aiandtechtoday.com/" />
      </Helmet>

      <main className="page">
        {/* HERO */}
        <section className="hero ai-hero fade-in">
          <div className="hero__content">
            <h1>AI & Tech Today</h1>
            <p>
              AI & Tech Today is an educational platform designed to help you
              understand and use modern artificial intelligence tools in a
              practical, responsible, and realistic way. Instead of hype or
              technical jargon, we focus on clear explanations and real-world
              use cases.
            </p>
            <p>
              Whether you are exploring AI for the first time or looking to
              improve how you already use these tools, our goal is to make AI
              more approachable and useful in everyday work, study, and
              creative projects.
            </p>
          </div>

          <div className="hero__image-wrapper">
            <img
              src="/images/hero-ai-dashboard.png"
              alt="Abstract AI dashboard with charts and connected nodes"
              className="hero__image"
              loading="eager"
            />
          </div>
        </section>

        {/* INTRODUCTION */}
        <section className="section">
          <h2>Understanding AI in everyday life</h2>
          <p>
            Artificial intelligence is no longer limited to research labs or
            large technology companies. In 2025, AI tools are used daily by
            students, professionals, creators, and small businesses to save
            time, organize information, and solve problems more efficiently.
          </p>

          <p>
            From writing emails and summarizing documents to generating code,
            images, and videos, AI assistants are becoming a standard part of
            modern digital workflows. However, many people still feel unsure
            about where to start or how to use these tools effectively.
          </p>

          <p>
            AI & Tech Today exists to bridge that gap. We explain what today’s
            AI tools can do, what their limitations are, and how you can apply
            them responsibly without needing a technical background.
          </p>
        </section>

        <section className="section">
          <h2>What’s happening in AI right now?</h2>
          <p>
            The current wave of AI innovation is largely driven by generative
            models and AI assistants. These systems are capable of producing
            human-like text, analyzing large amounts of information, and
            assisting with complex tasks through conversational interfaces.
          </p>

          <ul className="bullet-list">
            <li>
              <strong>Generative AI</strong> tools can create text, images,
              video, audio, and software code based on user prompts.
            </li>
            <li>
              <strong>AI assistants</strong> such as ChatGPT, Gemini, and Claude
              are commonly used for research, writing, learning, and planning.
            </li>
            <li>
              <strong>Automation and agent-style tools</strong> are beginning to
              handle multi-step tasks, such as organizing data or coordinating
              workflows.
            </li>
          </ul>

          <p>
            While these tools are powerful, they are not perfect. Understanding
            their strengths and limitations is essential for using them safely
            and effectively — a topic we cover throughout this site.
          </p>
        </section>

        {/* 🔹 AD SLOT */}
        <section className="ad-section">
          <Ad slot="home-mid-1" />
        </section>

        {/* TOOLS */}
        <section className="section section--cards">
          <h2>Core AI tools you should know</h2>

          <div className="card-grid">
            <article className="card">
              <img
                src="/images/card-chatgpt.png"
                alt="ChatGPT interface"
                className="card__image"
                loading="lazy"
              />
              <div className="card__body">
                <h3>ChatGPT</h3>
                <p>
                  ChatGPT is a general-purpose AI assistant widely used for
                  writing, summarization, brainstorming ideas, and learning new
                  topics. It is often used as a productivity and research aid.
                </p>
              </div>
            </article>

            <article className="card">
              <img
                src="/images/card-gemini.png"
                alt="Google Gemini interface"
                className="card__image"
                loading="lazy"
              />
              <div className="card__body">
                <h3>Google Gemini</h3>
                <p>
                  Gemini is Google’s AI system, integrated into Search,
                  Workspace, and Android. It is designed to work across text,
                  images, and documents in a unified way.
                </p>
              </div>
            </article>

            <article className="card">
              <img
                src="/images/card-copilot.png"
                alt="Microsoft Copilot inside Office apps"
                className="card__image"
                loading="lazy"
              />
              <div className="card__body">
                <h3>Microsoft Copilot</h3>
                <p>
                  Copilot brings AI features into Microsoft 365 applications,
                  helping users summarize emails, analyze spreadsheets, and
                  draft documents using existing work data.
                </p>
              </div>
            </article>

            <article className="card">
              <img
                src="/images/card-claude.png"
                alt="Person researching with an AI assistant"
                className="card__image"
                loading="lazy"
              />
              <div className="card__body">
                <h3>Claude & creative tools</h3>
                <p>
                  Tools like Claude, Notion AI, Midjourney, and Runway focus on
                  long-form research, organization, and creative media such as
                  images and video.
                </p>
              </div>
            </article>
          </div>
        </section>

        {/* AFFILIATE DISCLOSURE */}
        <section className="section section--disclosure">
          <AffiliateDisclosureInline />
        </section>

        {/* AFFILIATE CTA */}
        <section className="section section--affiliate">
          <h2>Comparing AI tools</h2>
          <p>
            Some AI tools offer paid plans with additional features. If you are
            exploring advanced options, comparing pricing and capabilities can
            help you choose what best fits your needs.
          </p>
          <AffiliateCTA />
        </section>

        {/* 🔹 AD SLOT */}
        <section className="ad-section">
          <Ad slot="home-mid-2" />
        </section>

        {/* INTERNAL LINKS */}
        <section className="section section--highlight">
          <h2>Explore more topics</h2>
          <ul className="internal-links">
            <li>
              <Link to="/better-body-mind">Better Body & Mind</Link>
            </li>
          </ul>
        </section>

      </main>
    </>
  );
}
